import React, {useState} from 'react'
import SlickSlider from 'app/Components/Carousels/SlickSlider'
import styled from 'styled-components'
import Icon, {glyphs} from 'app/Components/Icon'
import {_, useI18n} from 'plugins/i18n'
import {Image} from 'plugins/images'
import {isServer} from 'stratokit/build'
import {useResponsive} from 'app/styles'
import CarouselLightbox from './LightBoxCarousel'

const StyledImage = styled(Image)`
	height: var(--height);
	width: auto;
	cursor: pointer;
`
const PreloadImage = styled(Image)`
	position: absolute;
	opacity: 0;
`
const SsrBox = styled.div`
	display: flex;
	height: var(--height);
	width: 100%;
	overflow-y: hidden;
	overflow-x: scroll;

	scrollbar-width: none; /* Firefox */
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	&::-webkit-scrollbar {
		/* WebKit */
		width: 0;
		height: 0;
	}
`

const SingleImage = ({
	value,
	nextValue = undefined,
	alt,
	onClick,
	...props
}) => {
	if (!value) return <div className="relative h-full pr-[0.2rem]" />
	return (
		<div className="relative h-full pr-[0.2rem]">
			{nextValue ? (
				<PreloadImage alt={alt} value={{...nextValue.upload, ...nextValue}} />
			) : null}
			<StyledImage
				alt={alt}
				value={{...value.upload, ...value}}
				{...props}
				onClick={onClick}
			/>
		</div>
	)
}

const StyledCarousel = styled(SlickSlider)`
	position: relative;
	top: 0;
	left: 0;
	overflow: hidden;
	height: var(--height);
	.slick-track {
		display: flex;
	}
	.slick-slide {
		height: var(--height);
		${p => p.margin && `margin-right: ${p.margin};`};
		> div {
			height: 100%;
		}
	}
	&.slick-slider {
		width: 100%;
	}
`

const Arrow = styled(Icon)`
	position: absolute;
	cursor: pointer;
	z-index: 1;
	height: 60px;
	width: 60px;
	top: calc(50% - 30px);
	${p => (p.left ? 'left' : 'right')}: 0;
`

// Strip slick class and style props
const NextArrow = ({className, style, ...props}) => (
	<Arrow {...props} glyph={glyphs.arrowRight} />
)

const PrevArrow = ({className, style, ...props}) => (
	<Arrow {...props} left glyph={glyphs.arrowLeft} />
)

const HousedetailSlideshow: React.FC<{
	readonly value?: {
		images?: {
			alt?: string
			tags?: string[]
			[key: string]: any
		}[]
	}
}> = ({value}) => {
	const {isMobile} = useResponsive('isMobile')
	const i18n = useI18n()
	const [currentImage, setCurrentImage] = useState(0)
	const [lightboxIsOpen, setLightboxIsOpen] = useState(false)

	const images = value?.images || []

	const handleOpenLightbox = (index: number) => {
		setCurrentImage(index)
		setLightboxIsOpen(true)
	}

	const handleCloseLightbox = () => {
		setLightboxIsOpen(false)
	}

	const settings = {
		margin: 3,
		arrows: true,
		variableWidth: true,
		draggable: false,
		useCSS: 1,
		slidesToScroll: 1,
		autoplay: false,
		pauseOnHover: 1,
		lazyLoad: 0,
		infinite: 1,
		slidesToShow: 1,
		nextArrow: <NextArrow />,
		prevArrow: <PrevArrow />,
	}

	return (
		<div
			className="flex w-full items-center justify-center"
			style={{
				'--height': isMobile ? '20rem' : '30rem',
				height: 'var(--height)',
				marginTop: '-5em',
			}}
		>
			{images.length ? (
				isServer ? (
					<SsrBox>
						{images.slice(0, 3).map((image, i) => (
							<SingleImage key={i} value={image} alt={image.alt} />
						))}
					</SsrBox>
				) : (
					<StyledCarousel {...settings} isMobile={isMobile}>
						{images.map((image, i) => {
							const alt =
								image.alt ||
								(image.tags?.[0] && i18n.getText(image.tags[0])) ||
								`House image ${i + 1}`
							return (
								<SingleImage
									key={i}
									value={image}
									nextValue={images[i + 1]}
									onClick={() => handleOpenLightbox(i)}
									alt={alt}
								/>
							)
						})}
					</StyledCarousel>
				)
			) : (
				<div className="flex w-full items-center justify-center">
					{_('NoImagesDefined')}
				</div>
			)}
			<CarouselLightbox
				images={images}
				isOpen={lightboxIsOpen}
				onClose={handleCloseLightbox}
				index={currentImage}
			/>
		</div>
	)
}
export default HousedetailSlideshow
